/* eslint-disable no-console */
import { initializeApp } from 'firebase/app'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { firebaseConfig } from './firebaseConfig.ts'
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  inMemoryPersistence,
  indexedDBLocalPersistence,
  setPersistence,
} from 'firebase/auth'
import { connectDatabaseEmulator, getDatabase } from 'firebase/database'
import {
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator,
  disableNetwork,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'
import { isSupported, getMessaging } from 'firebase/messaging'
import {
  EMULATOR_HTTP,
  emulatorHubDisplayBase,
  emulatorHubEmulatorsUrl,
  getEmulatorTlsHostname,
  toLocalhostHost,
  toSslPort,
} from '@/utils/emulatorSsl.ts'

const functionsRegion = 'us-central1'

const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true'
const useEmulatorSsl = useEmulator && import.meta.env.VITE_USE_SSL === 'true'

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Initialize Firebase Functions
const functions = getFunctions(app, functionsRegion)
const database = getDatabase(app)
const auth = getAuth(app)

const cacheEnabled = import.meta.env.VITE_ENABLE_FIRESTORE_CACHE === 'true'
const firestoreDbName = import.meta.env.VITE_FIRESTORE_DB_NAME

const firestore = (() => {
  if (useEmulatorSsl) {
    const sslSettings = {
      host: `${getEmulatorTlsHostname()}:${toSslPort(EMULATOR_HTTP.firestore)}`,
      ssl: true as const,
      ...(cacheEnabled
        ? {
            localCache: persistentLocalCache({
              tabManager: persistentMultipleTabManager(),
              cacheSizeBytes: CACHE_SIZE_UNLIMITED,
            }),
          }
        : {}),
    }
    return initializeFirestore(app, sslSettings, firestoreDbName)
  }
  if (cacheEnabled) {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
        cacheSizeBytes: CACHE_SIZE_UNLIMITED,
      }),
    })
  }
  return getFirestore(app, firestoreDbName)
})()

const storage = getStorage(app)

/**
 * Emulator `connect*` calls must run synchronously before any Auth/Firestore/Storage use.
 * Ports match `firebase.json` → `emulators` ({@link EMULATOR_HTTP}).
 */
function connectFirebaseEmulatorsSync(): void {
  const tlsHost = getEmulatorTlsHostname()
  const httpHost = 'localhost'
  const scheme = useEmulatorSsl ? 'https' : 'http'

  const functionsHost = useEmulatorSsl ? tlsHost : httpHost
  const functionsPort = useEmulatorSsl
    ? toSslPort(EMULATOR_HTTP.functions)
    : EMULATOR_HTTP.functions
  connectFunctionsEmulator(functions, functionsHost, functionsPort)
  if (useEmulatorSsl) {
    // Workaround: Firebase JS SDK has no public API to force HTTPS for the Functions
    // emulator when we proxy emulators behind TLS. `emulatorOrigin` is internal but
    // required so callable/https requests match the SSL proxy URL; revisit if the SDK
    // adds official emulator protocol configuration.
    const fn = functions as Functions & { emulatorOrigin?: string }
    fn.emulatorOrigin = `https://${functionsHost}:${functionsPort}`
  }
  console.warn(`Using Firebase Functions emulator at ${scheme}://${functionsHost}:${functionsPort}`)

  const authHost = useEmulatorSsl ? tlsHost : httpHost
  const authPort = useEmulatorSsl ? toSslPort(EMULATOR_HTTP.auth) : EMULATOR_HTTP.auth
  connectAuthEmulator(auth, `${scheme}://${authHost}:${authPort}`, { disableWarnings: true })
  console.warn(`Using Firebase Auth emulator at ${scheme}://${authHost}:${authPort}`)

  if (!useEmulatorSsl) {
    connectFirestoreEmulator(firestore, httpHost, EMULATOR_HTTP.firestore)
    console.warn(
      `Using Firebase Firestore emulator at http://${httpHost}:${EMULATOR_HTTP.firestore}`,
    )
  } else {
    const firestorePort = toSslPort(EMULATOR_HTTP.firestore)
    console.warn(
      `Using Firebase Firestore emulator at https://${tlsHost}:${firestorePort} (SSL proxy)`,
    )
  }

  const storageHost = useEmulatorSsl ? tlsHost : httpHost
  const storagePort = useEmulatorSsl ? toSslPort(EMULATOR_HTTP.storage) : EMULATOR_HTTP.storage
  connectStorageEmulator(storage, storageHost, storagePort)
  if (useEmulatorSsl) {
    // Workaround: same TLS-proxy setup as Functions above — `_protocol` is not a public
    // Storage API but prevents the client from downgrading to http against the emulator.
    const storageInternal = storage as FirebaseStorage & { _protocol?: string }
    storageInternal._protocol = 'https'
  }
  console.warn(`Using Firebase Storage emulator at ${scheme}://${storageHost}:${storagePort}`)
}

if (useEmulator) {
  connectFirebaseEmulatorsSync()
}

const authReady = (async () => {
  try {
    if (typeof indexedDB !== 'undefined') {
      await setPersistence(auth, indexedDBLocalPersistence)
      return
    }
  } catch (error) {
    console.warn('Firebase auth persistence failed with IndexedDB2.', error)
  }

  try {
    await setPersistence(auth, browserLocalPersistence)
  } catch (error) {
    console.warn('Firebase auth persistence failed with localStorage.', error)
    await setPersistence(auth, inMemoryPersistence)
  }
})()

if (useEmulator) {
  void fetch(emulatorHubEmulatorsUrl(useEmulatorSsl))
    .then(async (hubResponse) => {
      if (!hubResponse.ok) {
        throw new Error(`Emulator Hub returned status ${hubResponse.status}`)
      }
      const emulators = await hubResponse.json()
      console.warn('Connected to Emulator Hub:', emulators)

      if (emulators.database && !useEmulatorSsl) {
        connectDatabaseEmulator(
          database,
          toLocalhostHost(emulators.database.host),
          emulators.database.port,
        )
        console.warn(
          `Using Firebase Database emulator at http://${toLocalhostHost(emulators.database.host)}:${emulators.database.port}`,
        )
      } else if (emulators.database && useEmulatorSsl) {
        console.warn(
          'Realtime Database emulator is not proxied over HTTPS in this setup; skipping connectDatabaseEmulator to avoid mixed content.',
        )
      }
    })
    .catch((error) => {
      const hubBase = emulatorHubDisplayBase(useEmulatorSsl)
      console.warn(
        `Emulator Hub health check failed at ${hubBase} (app uses fixed local ports from firebase.json):`,
        error,
      )
    })
}

// Initialize messaging as a promise to avoid top-level await
const messagingPromise = isSupported().then((supported) => {
  if (!supported) {
    return null
  }
  const messaging = getMessaging(app)
  return messaging
})

if (import.meta.env.VITE_DISABLE_FIRESTORE_NETWORK === 'true') {
  // Wrap in async IIFE to avoid top-level await
  ;(async () => {
    console.warn('Disabling network for firestore')
    await disableNetwork(firestore)
  })().catch((error) => {
    console.error('Failed to disable Firestore network:', error)
  })
}

export { app, functions, auth, authReady, firestore, storage, messagingPromise }
